import { useCallback, useEffect, useState } from 'react';
import Cropper, { type Area, type Point } from 'react-easy-crop';
import VideoCaptionLayer from './VideoCaptionLayer';
import type { VideoCaption } from '../../lib/videoCaption';

/** Instagram feed aspect ratios (width / height). */
export const IG_ASPECT_RATIOS = {
  '1:1': 1,
  '4:5': 4 / 5,
  '1.91:1': 1.91,
} as const;

export type IgAspectKey = keyof typeof IG_ASPECT_RATIOS;

export const IG_ASPECT_OPTIONS: { id: IgAspectKey; label: string }[] = [
  { id: '4:5', label: 'Portrait' },
  { id: '1:1', label: 'Square' },
  { id: '1.91:1', label: 'Landscape' },
];

export type SocialCropState = {
  crop: Point;
  zoom: number;
  croppedAreaPixels: Area | null;
};

export function defaultSocialCropState(): SocialCropState {
  return { crop: { x: 0, y: 0 }, zoom: 1, croppedAreaPixels: null };
}

export interface SocialImageCropEditorProps {
  imageUrl?: string;
  /** When set, the cropper frames a video instead of an image. */
  videoUrl?: string;
  aspect: number;
  value: SocialCropState;
  onChange: (next: SocialCropState) => void;
  className?: string;
  /** Max preview frame size — defaults keep the compact side-sheet fit. */
  previewMaxWidth?: number;
  previewMaxHeight?: number;
  /** Hide the zoom slider (pinch/wheel zoom still works). */
  hideZoomSlider?: boolean;
  /** Shown on the frame, e.g. "9:16", so the chosen ratio is visible. */
  ratioLabel?: string;
  /** Text burned on top of a video. Stays fixed while the video is dragged. */
  caption?: VideoCaption;
  onCaptionChange?: (next: VideoCaption) => void;
  captionSelected?: boolean;
  onCaptionSelectedChange?: (selected: boolean) => void;
  captionDisabled?: boolean;
}

/**
 * Instagram-style pan/zoom cropper for a single slide.
 * Parent owns aspect ratio (shared across carousel slides).
 */
export default function SocialImageCropEditor({
  imageUrl,
  videoUrl,
  aspect,
  value,
  onChange,
  className = '',
  previewMaxWidth = 280,
  previewMaxHeight = 240,
  hideZoomSlider = false,
  ratioLabel,
  caption,
  onCaptionChange,
  captionSelected = false,
  onCaptionSelectedChange,
  captionDisabled = false,
}: SocialImageCropEditorProps) {
  const [mediaReady, setMediaReady] = useState(false);

  useEffect(() => {
    setMediaReady(false);
  }, [imageUrl, videoUrl]);

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      onChange({ ...value, croppedAreaPixels });
    },
    [onChange, value],
  );

  // Cap preview so the full crop frame fits in the container (esp. 4:5 portrait).
  const previewHeight = Math.min(previewMaxHeight, previewMaxWidth / aspect);
  const previewWidth = previewHeight * aspect;

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex justify-center">
        <div
          className="relative overflow-hidden rounded-xl bg-black max-w-full"
          style={{
            width: previewWidth,
            height: previewHeight,
          }}
        >
          <Cropper
            key={videoUrl || imageUrl || 'media'}
            {...(videoUrl ? { video: videoUrl } : { image: imageUrl })}
            crop={value.crop}
            zoom={value.zoom}
            aspect={aspect}
            onCropChange={(crop) => onChange({ ...value, crop })}
            onZoomChange={(zoom) => onChange({ ...value, zoom })}
            onCropComplete={onCropComplete}
            onMediaLoaded={() => setMediaReady(true)}
            showGrid
            objectFit={videoUrl ? 'cover' : 'contain'}
            mediaProps={
              videoUrl
                ? { muted: true, autoPlay: true, loop: true, playsInline: true }
                : {}
            }
            style={{
              containerStyle: { width: '100%', height: '100%' },
            }}
          />
          {ratioLabel ? (
            <span className="absolute top-2.5 left-2.5 z-10 rounded-full bg-black/65 px-2 py-0.5 text-[11px] font-semibold text-white pointer-events-none">
              {ratioLabel}
            </span>
          ) : null}
          {videoUrl && caption && onCaptionChange ? (
            // The frame box is sized to the aspect, so the crop area fills it:
            // the caption layer can use the same CSS size as the frame.
            <VideoCaptionLayer
              caption={caption}
              width={previewWidth}
              height={previewHeight}
              onChange={onCaptionChange}
              selected={captionSelected}
              onSelectedChange={(next) => onCaptionSelectedChange?.(next)}
              disabled={captionDisabled}
            />
          ) : null}
          {!mediaReady ? (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-xs text-white">
              {videoUrl ? 'Loading video…' : 'Loading image…'}
            </div>
          ) : null}
        </div>
      </div>
      {!hideZoomSlider ? (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5">Zoom</label>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={value.zoom}
            onChange={(e) => onChange({ ...value, zoom: Number(e.target.value) })}
            className="w-full accent-blue-600"
          />
        </div>
      ) : null}
    </div>
  );
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image for crop'));
    img.src = url;
  });
}

/** Centered cover crop for an aspect when the interactive cropper has not emitted pixels yet. */
export function coverCropArea(
  imageWidth: number,
  imageHeight: number,
  aspect: number,
): Area {
  const imgAspect = imageWidth / Math.max(1, imageHeight);
  if (imgAspect > aspect) {
    const height = imageHeight;
    const width = height * aspect;
    return {
      width,
      height,
      x: (imageWidth - width) / 2,
      y: 0,
    };
  }
  const width = imageWidth;
  const height = width / aspect;
  return {
    width,
    height,
    x: 0,
    y: (imageHeight - height) / 2,
  };
}

/**
 * Canonical Instagram feed output sizes (exact ratios Meta accepts).
 * Avoids float/round drift from cropper pixels that Ayrshare rejects.
 */
export function igOutputSizeForAspect(
  aspect: number,
  maxEdge = 1080,
): { width: number; height: number } {
  const edge = Math.max(320, Math.min(1440, Math.round(maxEdge)));
  if (Math.abs(aspect - 1) < 0.02) {
    return { width: edge, height: edge };
  }
  if (aspect < 1) {
    // Portrait (e.g. 4:5) — height is the long edge.
    const height = edge;
    const width = Math.max(1, Math.round(height * aspect));
    return { width, height };
  }
  // Landscape (e.g. 1.91:1) — width is the long edge.
  const width = edge;
  const height = Math.max(1, Math.round(width / aspect));
  return { width, height };
}

/** Bake a cropped region to a JPEG blob (for upload before publish/stage). */
export async function bakeCroppedImage(
  imageUrl: string,
  croppedAreaPixels: Area | null | undefined,
  options?: {
    mimeType?: string;
    quality?: number;
    maxEdge?: number;
    /** Target aspect — always used for output size so Instagram ratios stay exact. */
    aspect?: number;
  },
): Promise<Blob> {
  const img = await loadImage(imageUrl);
  const mimeType = options?.mimeType || 'image/jpeg';
  const quality = options?.quality ?? 0.92;
  const maxEdge = options?.maxEdge ?? 1080;
  const aspect = options?.aspect && options.aspect > 0 ? options.aspect : 1;

  const area =
    croppedAreaPixels ||
    coverCropArea(img.naturalWidth, img.naturalHeight, aspect);

  let { width, height, x, y } = area;
  width = Math.max(1, Math.round(width));
  height = Math.max(1, Math.round(height));
  x = Math.max(0, Math.min(img.naturalWidth - 1, Math.round(x)));
  y = Math.max(0, Math.min(img.naturalHeight - 1, Math.round(y)));
  // Clamp crop rect inside the image (react-easy-crop can overshoot by 1px).
  if (x + width > img.naturalWidth) width = img.naturalWidth - x;
  if (y + height > img.naturalHeight) height = img.naturalHeight - y;
  width = Math.max(1, width);
  height = Math.max(1, height);

  const { width: outW, height: outH } = igOutputSizeForAspect(aspect, maxEdge);

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not available');

  ctx.drawImage(img, x, y, width, height, 0, 0, outW, outH);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('Failed to encode cropped image'));
        else resolve(blob);
      },
      mimeType,
      quality,
    );
  });
}
